// Android / browser Back for the in-app screens (2026-10-20).
//
// RitmoIQ's screens are React state, not URLs, so Back needs history entries of our own. The previous design
// pushed one "sentinel" entry on load and re-pushed it inside popstate. Chrome (also the installed Android
// PWA) SKIPS history entries that were added without a user activation ("history manipulation intervention"),
// so Back sometimes jumped straight out of the app.
//
// Rules here:
//   * An entry is pushed only WITH a user activation: immediately when the browser reports one, otherwise it
//     is DEFERRED to the next real user interaction (pointerup / touchend / keydown / mousedown). A programmatic
//     or async transition (onboarding completion, OAuth return, reload) never pushes without one.
//   * Our entries form a stack above the page's own entry: 'page' (an inner screen — at most one), 'guard'
//     (unsaved changes) and 'layer' (a modal / sheet). Home has no entry, so Back on Home exits normally.
//   * Back pops the top entry and runs ITS handler: a layer closes its modal; a guard asks the existing
//     confirmation; a page returns Home (through the unsaved-changes guard). Nothing ever re-pushes from
//     inside popstate — an entry that must come back is re-armed for the next user interaction.
//   * Entries released by the app (a modal closed by its own button, Home reached by a button) are removed
//     with one history.go(-n), coalesced, and the resulting popstate is ignored. No loops, no stacking.
let stack = [];            // entries we pushed and that are still in history, bottom -> top: {kind, id, live}
let pending = [];          // entries waiting for a user activation: {kind, id}
const handlers = new Map(); // id -> back handler: () => 'stay' | 'done'
let ignorePops = 0;        // popstates caused by our own history.go(-n)
let flushTimer = null;
let installed = false;
let seq = 0;
let activationListenerOn = false;

const ACTIVATION_EVENTS = ['pointerup', 'touchend', 'keydown', 'mousedown'];

function userIsActive() {
  try {
    const ua = typeof navigator !== 'undefined' && navigator.userActivation;
    return ua ? !!ua.isActive : true;          // no API (old browser / jsdom): treat the call as user-driven
  } catch {
    return true;
  }
}

function pushNow(kind, id) {
  try { window.history.pushState({ ritmoBack: kind, id }, ''); } catch { return; }
  stack.push({ kind, id, live: true });
}

function onActivation() {
  const toPush = pending;
  pending = [];
  toPush.forEach((p) => pushNow(p.kind, p.id));
  stopActivationListener();
}

function startActivationListener() {
  if (activationListenerOn) return;
  activationListenerOn = true;
  ACTIVATION_EVENTS.forEach((e) => window.addEventListener(e, onActivation, true));
}

function stopActivationListener() {
  if (!activationListenerOn) return;
  activationListenerOn = false;
  ACTIVATION_EVENTS.forEach((e) => window.removeEventListener(e, onActivation, true));
}

function arm(kind, id) {
  if (stack.some((e) => e.id === id && e.live) || pending.some((p) => p.id === id)) return;
  if (userIsActive()) pushNow(kind, id);
  else { pending.push({ kind, id }); startActivationListener(); }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    let k = 0;
    while (stack.length && !stack[stack.length - 1].live) { stack.pop(); k += 1; }
    if (k > 0) {
      ignorePops += 1;
      try { window.history.go(-k); } catch { ignorePops -= 1; }
    }
  }, 0);
}

function release(id) {
  const before = pending.length;
  pending = pending.filter((p) => p.id !== id);
  if (!pending.length && before) stopActivationListener();
  const entry = stack.find((e) => e.id === id && e.live);
  if (entry) { entry.live = false; scheduleFlush(); }
}

function onPopState() {
  if (ignorePops > 0) { ignorePops -= 1; return; }
  if (!stack.length) return;                          // not ours (e.g. Back on Home) — the browser decides
  const top = stack.pop();
  if (!top.live) { scheduleFlush(); return; }         // a just-released entry (rare race): consume quietly
  const h = handlers.get(top.id);
  const result = h ? h() : 'done';
  if (result === 'stay') arm(top.kind, top.id);       // re-armed for the NEXT user interaction, never here
  // after a modal closes, any released entries now on top go too
  if (stack.length && !stack[stack.length - 1].live) scheduleFlush();
}

function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('popstate', onPopState);
}

// ── public API ───────────────────────────────────────────────────────────────────────────────────────
/** Register a back entry while `active`. kind: 'page' | 'guard' | 'layer'. handler(): 'stay' keeps the entry
 *  (it is re-armed for the next user interaction), anything else consumes it. Returns a release function. */
export function registerBackEntry(kind, handler) {
  install();
  seq += 1;
  const id = `${kind}-${seq}`;
  handlers.set(id, handler);
  arm(kind, id);
  return () => { handlers.delete(id); release(id); };
}

/** Test / diagnostics only. */
export function _backNavState() {
  return { stack: stack.map((e) => `${e.kind}${e.live ? '' : '(released)'}`), pending: pending.map((p) => p.kind), ignorePops };
}

/** Test only: forget everything (each test starts with a clean history model). */
export function _resetBackNav() {
  stack = []; pending = []; handlers.clear(); ignorePops = 0;
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  stopActivationListener();
}
